import { useRef } from 'react';

import { CircleHelpIcon, DatabaseIcon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router';
import { toast } from 'sonner';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  SidebarWrapper,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { useAccount } from '@/hooks/useAccount';
import useBrains from '@/hooks/useBrains';
import useCollections from '@/hooks/useCollections';
import useMembers from '@/hooks/useMembers';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { formatDate, timeFromNow } from '@/util/dateUtils';
import { clampPercentage, isUnlimited } from '@/util/limits';

import { ConnectAIAgentForm, ConnectAIAgentFormHandle } from './ConnectAIAgentForm';
import { UpgradePlanDescription } from './UpgradePlanDescription';

export const DatasourceDetails = () => {
  // Redux selectors
  const slug = useSelector(selectAccountSlug);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );

  // Custom hooks
  const { collectionId } = useParams();
  const { collection } = useCollections(collectionId);
  const { t } = useTranslation();
  const { brainsByLanguage, updateBrain } = useBrains();
  const { totalFragments } = useCollections();
  const { members = [] } = useMembers();
  const { account } = useAccount();
  const dispatch = useDispatch();
  const connectFormRef = useRef<ConnectAIAgentFormHandle>(null);
  // Local variables
  const connectedBrains = brainsByLanguage(collection?.language)?.filter(
    (b) => b.collection_id === collectionId
  );
  const createdByUser = members.find((member) => member.user_id === collection?.created_by);
  const fragmentsPercentage = clampPercentage(totalFragments, account?.max_fragments);

  const handleDisconnectAiAgent = (agentId: string) => {
    updateBrain(
      { brain_id: agentId, collection_id: null },
      {
        onSuccess: ({ name }) => {
          toast.success(t('collections.toast_messages.ai_agent_disconnected', { 0: name }));
        },
      }
    );
  };

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">{t('common.details')}</h2>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          type="button"
          onClick={() => dispatch(setSidebarContent(null))}
        >
          <XIcon size={16} className="text-muted-foreground" />
        </Button>
      </SidebarWrapperTitle>

      <div className="flex flex-1 flex-col overflow-scroll">
        <div className="flex-1 space-y-4 p-6">
          {/* Collection Information */}
          <div className="flex flex-col space-y-4">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm leading-5 font-medium text-muted-foreground">
                {t('common.created_by')}
              </span>
              <span className="gap-4 truncate text-sm leading-6 font-semibold">
                {createdByUser?.name}
              </span>
            </div>

            <div className="flex items-center justify-between gap-4">
              <span className="text-sm leading-5 font-medium text-muted-foreground">
                {t('common.last_used')}
              </span>
              <span className="text-sm leading-6 font-semibold">
                {collection?.last_used_at
                  ? formatDate(collection.last_used_at, 'dd MMM yyyy')
                  : '-'}
              </span>
            </div>

            <div className="flex items-center justify-between gap-4">
              <span className="text-sm leading-5 font-medium text-muted-foreground">
                {t('common.last_modified')}
              </span>
              <span className="text-sm leading-6 font-semibold">
                {collection?.updated ? (
                  <TimestampTooltip timestamp={collection.updated} side="left">
                    {timeFromNow(collection.updated)}
                  </TimestampTooltip>
                ) : (
                  '-'
                )}
              </span>
            </div>
          </div>
          {/* Divider */}
          <div className="h-px bg-border"></div>

          {/* Connected AI Agents */}
          <div className="mt-2 space-y-2">
            <div className="flex items-center justify-between pr-1.5">
              <div className="flex items-center gap-1">
                <h2 className="text-sm leading-5 font-semibold">
                  {t('collections.connected_ai_agents')}
                </h2>
                <InfoTooltip content={t('collections.connect_ai_agents_tooltip')} side="right">
                  <CircleHelpIcon size={12} className="text-muted-foreground" />
                </InfoTooltip>
              </div>
              <Popover>
                <InfoTooltip content={t('common.connect')} delayDuration={300}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      size="icon-xss"
                      disabled={!canWrite}
                      aria-label={t('brains.connect_ai_agents')}
                    >
                      <PlusIcon size={16} />
                    </Button>
                  </PopoverTrigger>
                </InfoTooltip>
                <PopoverContent
                  side="right"
                  align="start"
                  className="overflow-hidden p-0"
                  onInteractOutside={() => {
                    connectFormRef.current?.submitSelectedAgents();
                  }}
                >
                  <ConnectAIAgentForm ref={connectFormRef} />
                </PopoverContent>
              </Popover>
            </div>

            {connectedBrains?.map((agent) => (
              <div key={agent.brain_id} className="flex items-center justify-between gap-2">
                <Link
                  className="text-sm leading-5 font-medium text-primary"
                  to={`/${slug}/ai-agents/${agent.brain_id}`}
                >
                  {agent.name}
                </Link>

                {canWrite && (
                  <InfoTooltip content={t('brains.disconnect_brain')} delayDuration={300}>
                    <Button
                      aria-label={t('brains.disconnect_brain')}
                      className="hover:text-destructive hover:[&_svg]:text-destructive"
                      variant="ghost-neutral"
                      size="icon-sm"
                      type="button"
                      onClick={() => handleDisconnectAiAgent(agent.brain_id)}
                    >
                      <Trash2Icon size={16} />
                    </Button>
                  </InfoTooltip>
                )}
              </div>
            ))}

            {connectedBrains?.length === 0 && (
              <div className="text-sm leading-5 text-muted-foreground">
                {t('collections.no_connected_ai_agents')}
              </div>
            )}
          </div>
        </div>

        {/* Fragments Usage */}
        <div className="p-6">
          <div className="border-t py-6">
            <div className="max-w-lg space-y-1">
              <div className="mb-3 flex items-center gap-1">
                <DatabaseIcon size={16} />
                <h3 className="text-sm leading-6 font-semibold">
                  {t('collections.details.fragment_usage.title')}
                </h3>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between gap-3">
                  {!isUnlimited(account?.max_fragments) && (
                    <Progress className="max-w-[75%]" value={fragmentsPercentage} />
                  )}
                  <p className="text-xs leading-5 font-medium whitespace-nowrap">
                    {isUnlimited(account?.max_fragments)
                      ? t('collections.details.count', { count: totalFragments ?? 0 })
                      : `${fragmentsPercentage}%`}
                  </p>
                </div>

                {!isUnlimited(account?.max_fragments) && (
                  <UpgradePlanDescription
                    i18nKey="collections.details.fragment_usage.description"
                    values={{ 0: account?.max_fragments }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SidebarWrapper>
  );
};
